import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { AuftragStatus, AuftragTrefferSeite, Auftrag, ApiClient,
              KundeListe as KundeListeEintrag, Rechnung,
              Unternehmen } from "@kundeninfo/api-client";
import { AuftragDetail, auftragFormularFelder, auftragVorschauWerte,
         useAuftraegeUmfeld } from "../components/AuftragDetail.js";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { useAuftragAnlegen } from "../components/auftragAnlegen.js";
import type { Vorbelegung } from "../components/auftragAnlegen.js";
import { apiMeldung, entwuerfeGleich } from "../components/vorgangsdetail.js";
import { KundenListe } from "./KundenListe.js";
import type { KundenFehler } from "./KundenListe.js";
import type { AuftraegeClient } from "./KundeAuftraege.js";
import type { VerlassenWaechter } from "../router.js";
import { formatBetrag, formatDatum } from "../format.js";
import { AUFTRAG_STATUS_LABEL } from "../vorgangsstatus.js";
import { AUFTRAGS_FILTER, statusZuFilter } from "../bereichsfilter.js";
import type { AuftragsFilter } from "../bereichsfilter.js";
import { Filterkopf } from "../components/Filterkopf.js";
import type { FilterChip } from "../components/Filterkopf.js";
import { Objektliste, Objektzeile } from "@kundeninfo/ui";
import type { StatusArt } from "@kundeninfo/ui";
import "./BereichAuftraege.css";

/**
 * Der Auftrags-BEREICH: Spaltenliste links (Filterkopf, Auftragszeilen, Blätterung),
 * vollwertiges Lese-Detail rechts — die Gestalt seit dem 23.09.2026 (Entscheid Dirk am
 * Live-System, Aufgabe 6), Zeile für Zeile nach demselben Muster wie die Schwester
 * views/BereichAngebote.tsx. Was dort steht, gilt hier gleichlautend; deshalb nennt dieser
 * Kopf nur, was den AUFTRAG unterscheidet: DREI Status statt fünf, und keine Version.
 *
 * Die ANORDNUNG ist zweimal gewechselt und steht jetzt wieder, wo sie anfing: Spalte und
 * Detail bis zum 07.09.2026, dann „Filterkopf statt zweiter Spalte" (alles untereinander in
 * voller Breite), und seit dem 23.09.2026 wieder Spalte und Detail. Vom Umbau des 07.09.
 * BLEIBT, was sich dabei als richtig erwiesen hat: der Filterkopf als EIN Baustein für alle
 * vier Bereiche (er steht jetzt oben IN der Spalte, siehe `lage="spalte"`), der Filter in
 * der Adresse und die Detail-Bausteine. Was geht, ist allein das Untereinander — und mit
 * ihm der bereichseigene Sprung-Mechanismus (components/Bereichsfaltung.tsx), denn die
 * Faltung auf schmalem Gerät macht wieder der Rahmen.
 *
 * Die ZEILEN zeichnet seit dem 23.09.2026 der gemeinsame Baustein `Objektliste`/
 * `Objektzeile` aus @kundeninfo/ui — dieselbe Zeile wie in den Schwesterbereichen.
 *
 * **Warum ein Hook und keine Komponente.** Der Bereich füllt ZWEI Bereiche des Rahmens:
 * die Listenspalte (`MeSchale.list`) und die Detailfläche (`MeSchale.detail`). Beide teilen
 * sich einen Zustand — die Trefferseite, den gewählten Vorgang, das offene Formular. Eine
 * Komponente kann ihre Ausgabe nicht auf zwei Eigenschaften des Rahmens verteilen; zwei
 * Komponenten müssten den Zustand irgendwo darüber halten, und „irgendwo darüber" wäre
 * App.tsx.
 *
 * **Was der Bereich NICHT ist: ein zweites Detail.** Rechts steht buchstäblich derselbe
 * Baustein wie im Aufträge-Reiter der Akte (`components/AuftragDetail.tsx`), und angelegt
 * wird über denselben Hook (`components/auftragAnlegen.ts`) — samt der Vorbelegung aus
 * einem angenommenen Angebot. Was hier hinzukommt, ist ausschliesslich das Drumherum: die
 * Objektliste, die Kundenauswahl vor dem Anlegen und der Verweis „Zum Kunden".
 *
 * **Der Kundenbezug.** Ein Auftrag wird über SEINEN KUNDEN geschrieben
 * (`updateSalesOrder(kundeId, …)`) und im Regelfall auch gelesen. Die Kunden-Kennung steht
 * in der Trefferzeile der Objektliste; von dort nimmt der Bereich sie und merkt sie sich,
 * solange derselbe Vorgang offen ist (`bezug`).
 *
 * **Der RÜCKFALL für Lesezeichen** (Entscheid Dirk 06.09.2026) — wortgleich zur Schwester
 * in BereichAngebote.tsx, wo die Begründung ausgeschrieben steht: Steht die adressierte
 * Kennung NICHT auf der Trefferseite, holt der Bereich den Satz über seine eigene Kennung
 * (`getOrderById`, GET /api/orders/{id}); die Antwort trägt den Kundenkontext mit. Der
 * nachgeladene Satz füllt GENAU dieselben zwei Zustände wie eine Trefferzeile (`bezug`,
 * `satz`) und ist ab da von einem Listensatz nicht zu unterscheiden — EINE Quelle, kein
 * zweiter Zustandspfad. Die dreistufige Auskunft bleibt für „Lädt …", „gibt es nicht
 * (mehr)" (echte 404) und den Ladefehler.
 *
 * **BINDEND (Ruling im Baustein, AuftragDetail.tsx → `speichern`): Die Liste darf nicht neu
 * laden, solange ein Detail in Bearbeitung ist.** Die Version, mit der gespeichert wird,
 * stammt aus dem Satz, den dieser Bereich führt; ein Neuladen währenddessen ersetzte sie
 * unbemerkt durch die fremde und machte die optimistische Sperre wirkungslos. Deshalb
 * zweierlei: Der Satz wird NUR gelesen, wenn der Bereich ihn noch nicht hat oder wenn der
 * Baustein selbst darum bittet (`onNeuLaden`, das er erst nach `onSchliessen` ruft) — und
 * die Bedienelemente über der Liste (Suche, Filter, Blätterung, Zeilen) sind während der
 * Bearbeitung gesperrt, sodass sie gar nicht erst eine neue Abfrage auslösen können.
 *
 * **Die zwei Nähte des Auftrags.** Rückwärts endet hier die Angebot→Auftrag-Naht (Ruling
 * des Controllers vom 06.09.2026: bereichsintern statt über die Akte — siehe `ausAngebot`),
 * vorwärts beginnt hier die Auftrag→Rechnung-Naht (`onRechnungAusAuftrag`). Beide gehen
 * über App.tsx, weil beide die ADRESSE wechseln; der Bereich meldet nur.
 *
 * **CSS:** eigener Block `bauf__` für das Drumherum. Was der Baustein mitbringt, behält
 * seine Klassen (`kauf__` — herausgelöste Bestandsteile werden nicht umbenannt, Etappe 1,
 * Aufgabe 2); geteilt wird über Ansichten hinweg KEIN Block. Die ZEILE selbst trägt seit
 * dem 23.09.2026 den Block `ki-objekt__` des gemeinsamen Bausteins (packages/ui/src/liste/
 * Objektliste.css) und keinen eigenen mehr.
 */

/** Der Bereich braucht alles, was der Akten-Reiter braucht — plus die globale Suche und den
 *  Lesezeichen-Weg (`getOrderById`), den er als Rückfall geht. */
export type BereichAuftraegeClient =
  AuftraegeClient & Pick<ApiClient, "searchOrders" | "getOrderById">;

/** Die Kunden-Suchauswahl, die App.tsx ohnehin führt (dieselbe Liste, die im Kundenbereich
 *  in der Spalte steht). Der Bereich baut keine zweite — er zeichnet sie nur an einer
 *  anderen Stelle, nämlich als ersten Schritt des Anlegens. */
export type BereichAuftraegeKunden = {
  items: KundeListeEintrag[];
  loading: boolean;
  error: KundenFehler | null;
  search: string;
  onSearch: (wert: string) => void;
};

/**
 * Die Angebot→Auftrag-Naht, wie sie in DIESEM Bereich ankommt (Ruling des Controllers vom
 * 06.09.2026: Die Naht läuft bereichsintern).
 *
 * Bis Aufgabe 3 führte sie über die Kundenakte: Der Angebote-Reiter legte dort eine
 * Vorbelegung ab, die Akte wechselte auf den Aufträge-Reiter. Für den BEREICH wäre das ein
 * Umweg über einen Bildschirm, den man gerade verlassen hat — deshalb reicht der Angebots-
 * Bereich seine Vorbelegung an App.tsx, die Adresse wechselt auf `/auftraege`, und dieser
 * Bereich nimmt sie beim Betreten auf. Die Kundenakte bleibt davon UNBERÜHRT; ihr eigener
 * Weg über den Reiter besteht unverändert weiter.
 *
 * Der Kunde reist mit, weil er hier feststeht: Die Kundenwahl (sonst der erste Schritt des
 * Anlegens) entfällt, der Name dient der Platzhalter-Vorschau und der Überschrift.
 */
export type AuftragAusAngebot = {
  kundeId: string;
  kundeName: string;
  vorbelegung: Vorbelegung;
};

export type BereichAuftraegeProps = {
  /** Steht der Bereich gerade auf dem Bildschirm? Der Hook wird — Regeln der Hooks — auch
   *  an anderen Adressen gerufen; ohne diesen Riegel liefe seine Suche überall mit. */
  aktiv: boolean;
  client: BereichAuftraegeClient;
  /** Die sichtbaren Konzernunternehmen: Auswahlliste des Filters und Auflösung der Kürzel. */
  companies: Unternehmen[];
  /** Der geöffnete Auftrag aus der Adresse (`/auftraege/:id`) — `null` heisst „keiner". */
  auftragId: string | null;
  /**
   * Die Zeile, die die Liste als gewählt zeigt, solange KEIN Objekt offen ist — am Telefon
   * die zuletzt geöffnete, damit der Rückweg die Auswahl behält (App.tsx, Hausregel
   * „Scroll bei Neuzeichnen"). Weggelassen: keine.
   */
  markiertId?: string | null;
  kunden: BereichAuftraegeKunden;
  /** Die Vorbelegung aus einem angenommenen Angebot — `null`, wenn frei angelegt wird. */
  ausAngebot: AuftragAusAngebot | null;
  /** Aufgenommen heisst VERBRAUCHT: Der Bereich meldet das sofort, damit die Vorbelegung
   *  genau einen Sprung überlebt. Bliebe sie stehen, öffnete ein späterer Rücksprung auf
   *  den Bereich dieselbe Maske ein zweites Mal (dieselbe Verfallsregel, die KundenAkte
   *  über den Reiterwechsel legt). */
  onAusAngebotAufgenommen: () => void;
  /** Adresswechsel innerhalb des Bereichs. Der Bereich navigiert NICHT selbst — die
   *  Adresse kennt App.tsx (dasselbe Muster wie bei den globalen Listen zuvor). */
  onOeffnen: (auftragId: string | null) => void;
  /** „Zum Kunden": in die Akte, mit dem Herkunfts-Rückweg vom 05.09. */
  onZumKunden: (kundeId: string) => void;
  /**
   * Die Auftrag→Rechnung-Naht (Paket 3b-2). Der Baustein LEITET die Rechnung selbst ab
   * (`createInvoiceFromOrder`) und meldet den fertigen Entwurf; der Bereich reicht ihn samt
   * Kunden weiter, weil das Ziel die Adresse wechselt.
   *
   * Solange es keinen Rechnungs-BEREICH gibt (er kommt mit Aufgabe 5b), führt der Weg über
   * die Kundenakte — den Bestandsweg. Danach ist hier `/rechnungen/<id>` einzusetzen.
   */
  onRechnungAusAuftrag: (kundeId: string, rechnung: Rechnung) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten): Bei offener
   *  Bearbeitung fragt jede Navigation nach. Weglassbar — dann fragt niemand, was für
   *  einen Prüfharnisch ohne Router genau richtig ist. */
  /** Der gewählte Statusfilter aus der Adresse (`/auftraege/<filter>`) — `null` heisst
   *  „alle Aufträge". Einfach wählbar, weil der Server-Weg genau einen Status nimmt
   *  (siehe components/Filterkopf.tsx und bereichsfilter.ts). */
  filter: AuftragsFilter | null;
  /** Den Statusfilter wechseln. Er klappt den offenen Auftrag zu, weil er in der neuen
   *  Liste nicht stehen muss. */
  onFilter: (filter: AuftragsFilter | null) => void;
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
  /** Ist die Sitzung ADMIN? Reicht bis in den Kasten „Abrechnungsquellen" — dort darf ein
   *  ADMIN an nicht aktiven Aufträgen lösen (Entscheid Dirk 29.09.2026, Punkt 7).
   *  Weglassbar — dann nicht. */
  istAdmin?: boolean | undefined;
};

export type BereichAuftraegeTeile = {
  /** Für `MeSchale.list` — Filterkopf, Auftragszeilen, Blätterung. */
  spalte: ReactElement;
  /** Für `MeSchale.detail` — der gewählte Auftrag als Lese-Detail bzw. die Anlege-Maske. */
  detail: ReactElement;
  /**
   * Steht die Anlege-Maske offen? Auf dem iPhone muss der Rahmen dann das DETAIL zeigen,
   * obwohl die Adresse (`/auftraege`) sonst die Spalte zuerst zeigt — die Maske hat keine
   * eigene Adresse (siehe router.ts). Sie war zwischen dem 07. und dem 23.09.2026 nicht da,
   * weil es ohne Spalte nichts zu falten gab.
   */
  anlageOffen: boolean;
};

/**
 * Die Zuordnung Fachstatus → Chip-ART (der Baustein kennt nur fünf Arten, siehe
 * packages/ui/src/liste/Objektliste.tsx).
 *
 * Die FARBWAHL ist unverändert die von vorher (`bauf__status--*` in BereichAuftraege.css):
 * Ein aktiver Auftrag läuft, ein abgeschlossener ist gut, ein stornierter ist eine Warnung.
 * Als `Record` über den Statustyp getippt: Kommt ein Status hinzu, meldet das der Typprüfer.
 */
const STATUS_ART: Record<AuftragStatus, StatusArt> = {
  ACTIVE: "laeuft",
  COMPLETED: "ok",
  CANCELLED: "warnung",
};

/** Wie viele Treffer auf eine Seite gehen — dieselbe Zahl wie bisher in der globalen Liste. */
const JE_SEITE = 20;

/** Die Chips der Filterleiste: die Statuswerte in der Reihenfolge des Lebenslaufs, mit
 *  ihren Beschriftungen aus der Beschriftungstabelle (vorgangsstatus.ts) — nicht abgetippt,
 *  damit ein umbenannter Status hier nicht ein zweites Mal gepflegt werden muss. */
const CHIPS: readonly FilterChip<AuftragsFilter>[] = AUFTRAGS_FILTER.map((f) => (
  { id: f.id, label: AUFTRAG_STATUS_LABEL[f.status] }));

/** Der Kundenbezug eines geöffneten Auftrags — einmal aus der Trefferzeile aufgelöst und
 *  danach festgehalten, damit ein Filterwechsel in der Spalte das offene Detail nicht
 *  zuklappt (die Zeile fiele dann ja aus der Trefferseite). */
type Kundenbezug = { auftragId: string; kundeId: string; kundeName: string };

/**
 * Wohin das Anlegen zielt: der Kunde, unter dem der neue Auftrag entsteht, die dort
 * wählbaren Unternehmen und — wenn er aus einem Angebot abgeleitet wird — dessen
 * Vorbelegung.
 *
 * Eine gemeinsame Gestalt für BEIDE Wege (Kundenwahl und Angebots-Naht), statt zweier
 * nebeneinander geführter Zustände: Ab hier ist der Anlege-Weg derselbe, und zwei Zustände
 * hiessen zwei Gelegenheiten, dass sie auseinanderlaufen.
 */
type AnlageZiel = {
  kundeId: string;
  kundeName: string;
  /** Beim Weg über die Kundenwahl aus `relations` gefiltert (Ruling „nur Unternehmen mit
   *  Kundenbeziehung"); beim Weg aus dem Angebot dessen eines Unternehmen — der Auftrag
   *  gehört zwingend zu demselben. */
  companies: Unternehmen[];
  vorbelegung: Vorbelegung | null;
};

export function useBereichAuftraege(p: BereichAuftraegeProps): BereichAuftraegeTeile {
  const { client, companies, aktiv, auftragId, filter } = p;

  const [seite, setSeite] = useState<AuftragTrefferSeite | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState("");
  const [suche, setSuche] = useState("");
  const [offset, setOffset] = useState(0);
  /** Hochgezählt, wenn die Trefferseite einen Stand noch nicht kennen kann — nach dem
   *  Anlegen und nach jeder Änderung, die der Baustein meldet. */
  const [listeVersion, setListeVersion] = useState(0);

  const [bezug, setBezug] = useState<Kundenbezug | null>(null);
  const [satz, setSatz] = useState<Auftrag | null>(null);
  const [satzFehler, setSatzFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  /** WELCHE Kennung der Rückfall als „gibt es nicht (mehr)" zurückgemeldet hat — als
   *  Kennung, nicht als Ja/Nein, damit sie den Adresswechsel nicht überlebt (dieselbe
   *  Überlegung wie bei `bearbeitetId` darunter). */
  const [fehltId, setFehltId] = useState<string | null>(null);

  /**
   * WELCHER Vorgang gerade bearbeitet wird — als Kennung, nicht als Ja/Nein.
   *
   * Ein blosses `bearbeitung: boolean` überlebte den Adresswechsel (Befund aus Aufgabe 3,
   * Fixrunde 1): Wer aus der offenen Bearbeitung heraus zurückging (Rückweg im Kopf,
   * Zurück-Taste), landete auf der Objektliste — und die blieb für immer gesperrt, denn
   * ihr Riegel hängt genau an dieser Angabe und es gab keinen Bedienweg mehr, sie zu lösen.
   * Über die Kennung ist der Zustand an den geöffneten Vorgang GEBUNDEN: Ist keiner offen
   * (oder ein anderer), ist auch nichts in Bearbeitung. Dasselbe Muster führt der
   * Akten-Reiter mit `bearbeitetId`.
   */
  const [bearbeitetId, setBearbeitetId] = useState<string | null>(null);
  const bearbeitung = bearbeitetId !== null && bearbeitetId === auftragId;

  /** Der Anlege-Weg: `null` = zu, sonst der Schritt (Kunde wählen bzw. Formular). */
  const [anlageZiel, setAnlageZiel] = useState<AnlageZiel | null>(null);
  const [anlageOffen, setAnlageOffen] = useState(false);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Muster aus Aufgabe 3).
   *
   * Anlege-Maske und Meldungsstreifen sind Bearbeitungszustände ohne eigene Adresse. Blieben
   * sie über einen Menüwechsel hinweg stehen, käme man mit einer halb ausgefüllten Maske
   * zurück, die zu einer längst verlassenen Absicht gehört — und `anlageOffen` behauptete
   * währenddessen ausserhalb des Bereichs weiter, es stehe etwas im Detail (siehe
   * `showList` in App.tsx). Die Kennung des bearbeiteten Vorgangs räumt derselbe Effekt
   * mit ab, damit ein späterer Vorwärts-Sprung auf dieselbe Adresse nicht in einem
   * Formular landet, das niemand geöffnet hat.
   *
   * Und mit ihnen der adresslose Teil der FILTERLEISTE — Suchtext, Unternehmen,
   * Blätterung —, denn er ist genauso ein Bearbeitungszustand ohne eigene Adresse
   * (dieselbe Regel wie im Rechnungsbereich, wo sie zuerst gezogen wurde). Bliebe er
   * stehen, zeigte die Liste beim nächsten Betreten heimlich eine Teilmenge, ohne dass die
   * Adresse es sagte: Wer über das Menü zurückkommt, erwartet den Bereich, nicht den Rest
   * einer alten Suche. Der LADEFEHLER geht mit — ein Fehler von vorhin ist keine Auskunft
   * über den Bestand von jetzt.
   *
   * Der STATUSFILTER gehört seit dem 07.09.2026 NICHT mehr dazu: Er steht in der Adresse
   * und räumt sich damit von selbst ab — die Hauptnavigation führt auf `/auftraege`, also
   * auf „alle". Ihn hier zusätzlich zurückzusetzen hiesse, den Zustand an zwei Stellen zu
   * führen.
   */
  useEffect(() => {
    if (aktiv) return;
    setAnlageOffen(false);
    setAnlageZiel(null);
    setBearbeitetId(null);
    setHinweis(null);
    setSuche("");
    setCompanyId("");
    setOffset(0);
    setLadeFehler(null);
  }, [aktiv]);

  /*
   * Die Angebot→Auftrag-Naht AUFNEHMEN: Kundenwahl überspringen, Maske vorbelegt öffnen.
   *
   * Sofort als verbraucht melden — die Vorbelegung soll genau EINEN Sprung überleben (siehe
   * `onAusAngebotAufgenommen`). Der Effekt steht bewusst NACH dem Aufräum-Effekt darüber:
   * Beim Betreten des Bereichs läuft der zuerst und gibt sich mit `aktiv` zufrieden, sodass
   * er die eben gesetzte Maske nicht gleich wieder abräumt.
   */
  const ausAngebot = p.ausAngebot;
  const onAusAngebotAufgenommen = p.onAusAngebotAufgenommen;
  useEffect(() => {
    if (!aktiv || ausAngebot === null) return;
    setHinweis(null);
    setAnlageZiel({
      kundeId: ausAngebot.kundeId,
      kundeName: ausAngebot.kundeName,
      // Das Unternehmen des Angebots — beim Ableiten steht es fest und ist damit auch das
      // einzige, das die Maske anbieten darf.
      companies: companies.filter((c) => c.id === ausAngebot.vorbelegung.companyId),
      vorbelegung: ausAngebot.vorbelegung,
    });
    setAnlageOffen(true);
    onAusAngebotAufgenommen();
  }, [aktiv, ausAngebot, companies, onAusAngebotAufgenommen]);

  /**
   * Verlassen-Wächter (Hausmuster, siehe KundeStammdaten): Solange ein Vorgang offen
   * bearbeitet wird, fragt JEDE Navigation nach — der Rückweg im Kopf des Einspalters
   * ebenso wie die Zurück-Taste. Ohne ihn verwarf ein Rückweg die Eingaben stumm.
   *
   * Registriert wird am OFFENEN Formular, nicht erst an einer erkannten Änderung: Der
   * Entwurf lebt im Baustein (components/AuftragDetail.tsx), und der Bereich sieht ihn
   * nicht. Ihn dafür nach aussen zu reichen hiesse, den Baustein für einen seiner beiden
   * Verwender umzubauen — die Rückfrage einmal zu viel ist der kleinere Preis als eine
   * Änderung, die still verloren geht.
   *
   * Bestätigtes Verlassen räumt von selbst auf: Die Adresse wechselt, und damit passt
   * `bearbeitetId` nicht mehr zum geöffneten Vorgang.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!bearbeitung || registriereVerlassenWaechter === undefined) return;
    return registriereVerlassenWaechter(() => window.confirm(
      "Dieser Auftrag ist noch in Bearbeitung. Nicht gespeicherte Eingaben gehen "
      + "verloren. Wirklich verlassen?"));
  }, [bearbeitung, registriereVerlassenWaechter]);

  const searchOrders = client.searchOrders;
  /** Der Status hinter dem gewählten Filter — `undefined` heisst „alle" und geht gar nicht
   *  erst mit (ein leerer Wert wäre ein Filter auf „leer"). */
  const status = filter === null ? undefined : statusZuFilter(filter, AUFTRAGS_FILTER);

  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    // Nur GESETZTE Filter gehen mit: Ein leerer Wert wäre ein Filter auf „leer".
    searchOrders({
      ...(status === undefined ? {} : { status }),
      ...(companyId === "" ? {} : { companyId }),
      ...(suche === "" ? {} : { suche }),
      limit: JE_SEITE,
      offset,
    })
      .then((antwort) => { if (!abgebrochen) { setSeite(antwort); setLadeFehler(null); } })
      .catch(() => {
        if (!abgebrochen) {
          setLadeFehler("Die Aufträge konnten nicht geladen werden. "
            + "Bitte die Seite neu laden.");
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, searchOrders, status, companyId, suche, offset, listeVersion]);

  /**
   * Den Kundenbezug des geöffneten Auftrags aus der Trefferseite auflösen — EINMAL je
   * Vorgang. Steht er schon, bleibt er: Sonst risse ein Filterwechsel, der die Zeile aus
   * der Trefferseite drängt, das offene Detail wieder zu.
   */
  useEffect(() => {
    if (auftragId === null) { setBezug(null); return; }
    const zeile = seite?.items.find((t) => t.id === auftragId);
    setBezug((alt) => {
      if (alt !== null && alt.auftragId === auftragId) return alt;
      if (zeile === undefined) return null;
      return { auftragId, kundeId: zeile.customerId, kundeName: zeile.customerName };
    });
  }, [auftragId, seite]);

  /*
   * Den VOLLEN Vorgang lesen — die Trefferzeile trägt bewusst nur die magere Gestalt eines
   * Sucheinstiegs (kein `version`, keine Texte, keine Laufzeit).
   *
   * Der Riegel über `satzRef` ist die Umsetzung des Rulings aus dem Kopfkommentar: Gelesen
   * wird nur, was noch nicht da ist. Alles Weitere — nach dem Speichern, nach einem
   * Statuswechsel, nach einer 409 — stösst der Baustein selbst über `onNeuLaden` an, und
   * zwar erst, nachdem er die Bearbeitung geschlossen hat.
   */
  const satzRef = useRef<Auftrag | null>(null);
  satzRef.current = satz;
  useEffect(() => {
    if (!aktiv || bezug === null) { setSatz(null); setSatzFehler(null); return; }
    if (satzRef.current !== null && satzRef.current.id === bezug.auftragId) return;
    let abgebrochen = false;
    setSatz(null);
    setSatzFehler(null);
    client.getSalesOrder(bezug.kundeId, bezug.auftragId)
      .then((a) => { if (!abgebrochen) setSatz(a); })
      .catch((e) => {
        if (!abgebrochen) {
          setSatzFehler(apiMeldung(e, "Der Auftrag konnte nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, client, bezug]);

  /*
   * DER RÜCKFALL für Lesezeichen — wortgleich zur Schwester in BereichAngebote.tsx, wo die
   * drei Bedingungen und die bindende STELLUNG nach dem Satz-Lade-Effekt ausgeschrieben
   * stehen. Kurz: `bezugSteht` schützt den geführten Satz, `zeileVorhanden` verhindert, dass
   * der Rückfall bei jedem Zeilenklick parallel zur Auflösung aus der Zeile losschiesst, und
   * `listeSteht` sagt, ob überhaupt schon entschieden ist, dass die Zeile fehlt.
   */
  const bezugSteht = bezug !== null && bezug.auftragId === auftragId;
  const zeileVorhanden = seite !== null && seite.items.some((t) => t.id === auftragId);
  const listeSteht = seite !== null || ladeFehler !== null;
  useEffect(() => {
    if (!aktiv || auftragId === null || bezugSteht || zeileVorhanden || !listeSteht) return;
    let abgebrochen = false;
    // Vor dem Versuch beide Auskünfte des VORIGEN zurücknehmen (siehe die Schwester in
    // BereichAngebote.tsx): Ein zweiter Anlauf auf dieselbe Adresse darf nicht unter einem
    // stehengebliebenen „gibt es nicht (mehr)" laufen.
    setFehltId(null);
    setSatzFehler(null);
    client.getOrderById(auftragId)
      .then((a) => {
        if (abgebrochen) return;
        // GENAU die beiden Zustände, die auch eine Trefferzeile füllt (EINE Quelle).
        setBezug({ auftragId: a.id, kundeId: a.customerId, kundeName: a.customerName });
        setSatz(a);
      })
      .catch((e) => {
        if (abgebrochen) return;
        // Die 404 heisst „gibt es nicht ODER darfst du nicht sehen" — die API unterscheidet
        // das bewusst nicht, und die Auskunft hier tut es deshalb auch nicht.
        if (e instanceof ApiError && e.status === 404) setFehltId(auftragId);
        else setSatzFehler(apiMeldung(e, "Der Auftrag konnte nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [aktiv, client, auftragId, bezugSteht, zeileVorhanden, listeSteht]);

  /** Der Baustein hat den Vorgang weitergedreht (Version/Betrag aus dem Positions-Editor).
   *  Er bleibt HIER stehen — kein Server-Abruf, siehe das Ruling im Kopf. */
  function fortschreiben(neu: Auftrag): void {
    setSatz(neu);
  }

  /** Der Baustein bittet um einen frischen Stand. Erst dann — und nur dann — wird der
   *  Vorgang neu gelesen und die Trefferseite nachgezogen. */
  async function satzNeuLaden(): Promise<void> {
    if (bezug === null) return;
    setListeVersion((v) => v + 1);
    try {
      setSatz(await client.getSalesOrder(bezug.kundeId, bezug.auftragId));
      setSatzFehler(null);
    } catch (e) {
      setSatzFehler(apiMeldung(e, "Der Auftrag konnte nicht geladen werden."));
    }
  }

  function anlageSchliessen(): void {
    setAnlageOffen(false);
    setAnlageZiel(null);
  }

  function anlageBeginnen(): void {
    setHinweis(null);
    setAnlageZiel(null);
    setAnlageOffen(true);
    // Der bisher geöffnete Vorgang wird auch in der ADRESSE zugeklappt: Sonst stünde
    // `/auftraege/<id>` über einer Maske, die einen anderen Vorgang anlegt, und die Spalte
    // hübe eine Zeile hervor, die gerade gar nicht zu sehen ist.
    p.onOeffnen(null);
  }

  /** Nach dem Anlegen aus einem Angebot: den neuen Vorgang gleich aufschlagen — seine
   *  Positionen sind schon darin (der Server kopiert sie mit), es fehlt nur noch die
   *  Laufzeit. Das ist zugleich das Ende der bereichsinternen Naht: Die Adresse steht
   *  danach auf `/auftraege/<neueId>`. */
  function neuBearbeiten(ziel: AnlageZiel, a: Auftrag, meldung: string): void {
    setBezug({ auftragId: a.id, kundeId: ziel.kundeId, kundeName: ziel.kundeName });
    setSatz(a);
    setBearbeitetId(a.id);
    setHinweis(meldung);
    p.onOeffnen(a.id);
  }

  /** Filter ändern heisst: zurück auf die erste Seite. Sonst stünde man nach dem Filtern
   *  auf Seite 7 eines Ergebnisses mit einer Seite und sähe nichts. */
  function filtern(setzen: () => void): void {
    setzen();
    setOffset(0);
  }

  /**
   * Den STATUSFILTER wechseln. Er steht in der Adresse, also navigiert der Bereich nicht
   * selbst — App.tsx tut es (`onFilter`) und klappt dabei den offenen Auftrag zu: Er muss
   * in der neuen Liste nicht stehen. Der SUCHTEXT bleibt stehen, die Blätterung geht auf
   * Seite 1 zurück (dieselbe Regel wie im Angebots-Bereich).
   */
  function filterWaehlen(neu: AuftragsFilter | null): void {
    setHinweis(null);
    setBearbeitetId(null);
    anlageSchliessen();
    setOffset(0);
    p.onFilter(neu);
  }

  /** Das Kürzel des Unternehmens zu einer Zeile. Fehlt die Firma (sie könnte stillgelegt
   *  sein), bleibt die Stelle leer statt eine nackte UUID zu zeigen. */
  function kuerzel(id: string): string {
    return companies.find((c) => c.id === id)?.shortCode ?? "";
  }

  const gefiltert = filter !== null || companyId !== "" || suche !== "";
  const von = seite === null || seite.total === 0 ? 0 : offset + 1;
  const bis = seite === null ? 0 : Math.min(offset + seite.items.length, seite.total);

  const spalte = (
    <div className="bauf">
      {/*
        Der FILTERKOPF (components/Filterkopf.tsx) — Suchfeld, Status-Chips und die
        Unternehmens-Auswahl als Zusatzfilter, in einem Baustein, den alle vier
        Objektbereiche teilen. Die Chips sind EINFACH wählbar: Der Server-Weg dieses
        Bereichs nimmt genau einen Status (siehe bereichsfilter.ts).

        `lage="spalte"` seit dem 23.09.2026 (Entscheid Dirk): Er steht oben in der
        Listenspalte des Rahmens.
      */}
      <Filterkopf
        lage="spalte"
        suchLabel="Aufträge durchsuchen"
        platzhalter="Nummer, Titel oder Kunde"
        suche={suche}
        onSuche={(t) => filtern(() => setSuche(t))}
        gesperrt={bearbeitung}
        chips={{
          label: "Aufträge nach Status",
          alle: "Alle Aufträge",
          werte: CHIPS,
          modus: "einfach",
          gewaehlt: filter,
          onGewaehlt: filterWaehlen,
        }}
        aktion={
          <button type="button" className="bauf__neu" onClick={anlageBeginnen}
            disabled={bearbeitung || anlageOffen} title="Neuen Auftrag anlegen">
            Neuer Auftrag
          </button>
        }
        zusatz={
          <div className="bauf__auswahl">
            <label className="bauf__label" htmlFor="bauf-firma">Unternehmen</label>
            <select id="bauf-firma" className="bauf__select" value={companyId}
              disabled={bearbeitung}
              onChange={(e) => filtern(() => setCompanyId(e.target.value))}>
              <option value="">alle</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
              ))}
            </select>
          </div>
        }
      />

      {/* Die AUFTRAGSLISTE. Sie ROLLT (siehe BereichAuftraege.css): Der Filterkopf bleibt
          oben stehen. */}
      <div className="bauf__liste">
        {ladeFehler !== null && (
          <p className="bauf__fehler" role="alert">{ladeFehler}</p>
        )}

        {ladeFehler === null && seite === null && (
          <p className="bauf__lade" role="status">Lädt …</p>
        )}

        {ladeFehler === null && seite !== null && seite.items.length === 0 && (
          <p className="bauf__hinweis">
            {gefiltert
              ? "Zu diesen Filtern gibt es keinen Auftrag."
              : "Es gibt noch keine Aufträge."}
          </p>
        )}

        {/*
          Die Zeilen tragen den gemeinsamen Baustein (Entscheid Dirk 23.09.2026) — dieselbe
          Anatomie wie in den drei Schwesterbereichen. Der Inhalt ist unverändert, nur in die
          Anatomie gelegt: Nummer als `kennung`, Titel als `name`, Status als Chip, und
          Kunde · Kürzel · Datum · Betrag als EIN Meta-Text.

          Eine Version gibt es hier nicht — die führen nur Angebote (Paket
          Angebotsversionen).
        */}
        <Objektliste label="Aufträge">
          {(seite?.items ?? []).map((a) => (
            <Objektzeile
              key={a.id}
              name={a.title}
              kennung={a.orderNumber}
              /* Ein leeres Kürzel (die Firma ist archiviert und fehlt darum in
                 `companies` — /api/session liefert nur aktive Unternehmen) darf keinen
                 leeren Trenner „·  ·" hinterlassen, darum die Teile filtern statt
                 aneinanderzuhängen (Befund Gesamtreview, 23.09.2026). */
              meta={[
                a.customerName,
                kuerzel(a.companyId),
                formatDatum(a.createdAt),
                formatBetrag(a.netAmount, "EUR"),
              ].filter((t) => t !== "").join(" · ")}
              status={{ text: AUFTRAG_STATUS_LABEL[a.status], art: STATUS_ART[a.status] }}
              gewaehlt={a.id === (auftragId ?? p.markiertId ?? null)}
              gesperrt={bearbeitung}
              /* Der zugängliche Name nennt Nummer und Kunden; zwanzig Zeilen „Öffnen" wären
                 für jemanden, der die Seite hört, nicht unterscheidbar. Er endet auf
                 „anzeigen": Der Klick adressiert das LESE-Detail. */
              ariaLabel={`Auftrag ${a.orderNumber}, ${a.customerName}, anzeigen`}
              onOeffnen={() => { setHinweis(null); anlageSchliessen(); p.onOeffnen(a.id); }}
            />
          ))}
        </Objektliste>

        {seite !== null && seite.total > 0 && (
          <div className="bauf__blaettern">
            <button type="button" className="bauf__knopf"
              disabled={offset === 0 || bearbeitung}
              onClick={() => setOffset((o) => Math.max(0, o - JE_SEITE))}>
              Zurück
            </button>
            <span className="bauf__zusatz">{von}–{bis} von {seite.total}</span>
            <button type="button" className="bauf__knopf"
              disabled={offset + seite.items.length >= seite.total || bearbeitung}
              onClick={() => setOffset((o) => o + JE_SEITE)}>
              Weiter
            </button>
          </div>
        )}
      </div>
    </div>
  );

  /*
   * Die Detailfläche bekommt bewusst KEINE eigene Landmarke: Der Rahmen zeichnet dort bereits
   * eine (MeSchale). Was hier je nach Zustand steht — Anlege-Maske, Kundenwahl —, trägt seine
   * eigene Beschriftung.
   */
  const detail = (
    <div className="bauf__inhalt">
      {hinweis !== null && <p className="bauf__meldung" role="status">{hinweis}</p>}
      {satzFehler !== null && <p className="bauf__fehler" role="alert">{satzFehler}</p>}

      {anlageOffen ? (
        anlageZiel === null ? (
          <section className="bauf__anlage" aria-label="Kunde wählen">
            <h2 className="bauf__anlage-titel">Neuer Auftrag</h2>
            <p className="bauf__anlage-schritt">
              Ein Auftrag gehört zu einem Kunden — bitte zuerst den Kunden wählen.
            </p>
            <div className="bauf__kundenwahl">
              <KundenListe
                items={p.kunden.items}
                loading={p.kunden.loading}
                error={p.kunden.error}
                search={p.kunden.search}
                onSearch={p.kunden.onSearch}
                selectedId={null}
                onSelect={(id) => {
                  const gewaehlt = p.kunden.items.find((k) => k.id === id);
                  if (gewaehlt === undefined) return;
                  setAnlageZiel({
                    kundeId: gewaehlt.id,
                    kundeName: gewaehlt.companyName,
                    // Nur die Unternehmen mit Kundenbeziehung sind wählbar (Ruling „nur
                    // Unternehmen mit Kundenbeziehung") — dieselbe Filterung wie in
                    // KundenAkte.tsx, über den shortCode.
                    companies: companies.filter(
                      (c) => gewaehlt.relations.some((r) => r.shortCode === c.shortCode)),
                    vorbelegung: null,
                  });
                }}
                companies={companies}
              />
            </div>
            <div className="kauf__formular-aktionen">
              <button type="button" className="kauf__sekundaer" onClick={anlageSchliessen}>
                Abbrechen
              </button>
            </div>
          </section>
        ) : (
          <AuftragAnlage
            // Eigene Identität je Ziel: Der Anlege-Hook liest Vorbelegung und wählbare
            // Unternehmen beim MOUNTEN (useState-Anfangswert). Ohne diesen Schlüssel
            // behielte ein Wechsel des Ziels den Entwurf des vorigen.
            key={`${anlageZiel.kundeId}:${anlageZiel.vorbelegung?.offerId ?? ""}`}
            client={client}
            ziel={anlageZiel}
            onAbbrechen={anlageSchliessen}
            onAnderenKunden={() => setAnlageZiel(null)}
            onAngelegt={() => { anlageSchliessen(); setListeVersion((v) => v + 1); }}
            onNeuLaden={async () => {
              setListeVersion((v) => v + 1);
              try {
                const { items } = await client.listSalesOrders(anlageZiel.kundeId);
                return items;
              } catch {
                return [];
              }
            }}
            onBearbeiten={(a, meldung) => neuBearbeiten(anlageZiel, a, meldung)}
          />
        )
      ) : auftragId === null ? (
        <p className="bauf__leer">
          Links einen Auftrag wählen — er steht dann hier, mit allem, was
          auch die Kundenakte zeigt.
        </p>
      ) : bezug === null ? (
        /*
         * Ohne Kundenbezug ist der Rückfall unterwegs oder gescheitert (siehe den Kopf
         * dieser Datei) — die dreistufige Auskunft, wortgleich zur Schwester im
         * Angebots-Bereich: „gibt es nicht (mehr)" nur bei echter 404, der Ladefehler steht
         * bereits als Alarm über dieser Fläche, sonst läuft noch etwas.
         */
        fehltId === auftragId ? (
          <p className="bauf__leer">
            Diesen Auftrag gibt es nicht (mehr) — oder er gehört zu einem Unternehmen,
            das hier nicht sichtbar ist.
          </p>
        ) : satzFehler !== null ? (
          <></>
        ) : (
          <p className="bauf__leer">Lädt …</p>
        )
      ) : satz === null ? (
        satzFehler === null ? <p className="bauf__leer">Lädt …</p> : <></>
      ) : (
        <AuftragBereichsDetail
          client={client}
          kundeId={bezug.kundeId}
          kundeName={bezug.kundeName}
          auftrag={satz}
          companies={companies}
          bearbeitung={bearbeitung}
          istAdmin={p.istAdmin === true}
          onZumKunden={() => p.onZumKunden(bezug.kundeId)}
          onBearbeiten={() => { setHinweis(null); setBearbeitetId(bezug.auftragId); }}
          onSchliessen={() => setBearbeitetId(null)}
          onNeuLaden={satzNeuLaden}
          onHinweis={setHinweis}
          onFortschreiben={fortschreiben}
          onRechnungAusAuftrag={(r) => p.onRechnungAusAuftrag(bezug.kundeId, r)}
        />
      )}
    </div>
  );

  return { spalte, detail, anlageOffen };
}


/**
 * Das Detail eines Auftrags im Bereich: der Kundenbezug als Verweis, darunter der
 * BAUSTEIN — derselbe, den der Aufträge-Reiter der Akte zeichnet.
 *
 * Eigene Komponente, damit `useAuftraegeUmfeld` erst läuft, wenn es einen Kunden GIBT: Der
 * Hook zieht Ansprechpartner, Benutzer, Konditionen und Platzhalter, und ohne
 * Kunden-Kennung wären das vier Abrufe ins Leere.
 */
function AuftragBereichsDetail(p: {
  client: BereichAuftraegeClient;
  kundeId: string;
  kundeName: string;
  auftrag: Auftrag;
  companies: Unternehmen[];
  bearbeitung: boolean;
  istAdmin: boolean;
  onZumKunden: () => void;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  onNeuLaden: () => Promise<void>;
  onHinweis: (text: string | null) => void;
  onFortschreiben: (auftrag: Auftrag) => void;
  onRechnungAusAuftrag: (rechnung: Rechnung) => void;
}): ReactElement {
  const umfeld = useAuftraegeUmfeld(p.client, p.kundeId);

  return (
    <div className="bauf__karte">
      <div className="bauf__detail-kopf">
        <span className="bauf__kunde">{p.kundeName}</span>
        {/* Der Kundenbezug bleibt SICHTBAR und begehbar: Die Akte ist weiterhin der eine
            Ort, an dem alles zu einem Kunden steht — der Bereich nimmt ihr nur die Pflicht,
            für jeden Vorgang der Umweg zu sein. Der Rückweg von dort kennt seine Herkunft
            (App.tsx, `zurueckWeg`). */}
        <button type="button" className="bauf__zum-kunden" onClick={p.onZumKunden}>
          Zum Kunden
        </button>
      </div>

      <AuftragDetail
        client={p.client}
        kundeId={p.kundeId}
        auftrag={p.auftrag}
        companies={p.companies}
        umfeld={umfeld}
        bearbeitung={p.bearbeitung}
        // Es gibt hier genau EIN Detail — was der Reiter mit `gesperrt` gegen seine
        // übrigen Zeilen absichert, kann im Bereich gar nicht auftreten.
        gesperrt={false}
        kundeName={p.kundeName}
        istAdmin={p.istAdmin}
        onBearbeiten={p.onBearbeiten}
        onSchliessen={p.onSchliessen}
        onNeuLaden={p.onNeuLaden}
        onHinweis={p.onHinweis}
        onFortschreiben={p.onFortschreiben}
        onRechnungAusAuftrag={p.onRechnungAusAuftrag} />
    </div>
  );
}

/**
 * Der zweite Schritt des Anlegens: dieselbe Maske wie im Akten-Reiter, geführt vom
 * gemeinsamen Hook (`useAuftragAnlegen`) — samt der Vorbelegung aus einem angenommenen
 * Angebot und dem Aufschlagen des neuen Vorgangs, wenn eine im Spiel war.
 *
 * Auch sie ist eine eigene Komponente, und aus demselben Grund wie das Detail: Erst mit
 * einem feststehenden Kunden gibt es etwas zu laden.
 */
function AuftragAnlage(p: {
  client: BereichAuftraegeClient;
  ziel: AnlageZiel;
  onAbbrechen: () => void;
  onAnderenKunden: () => void;
  onAngelegt: () => void;
  onNeuLaden: () => Promise<Auftrag[]>;
  onBearbeiten: (auftrag: Auftrag, meldung: string) => void;
}): ReactElement {
  const { ziel } = p;

  const anlage = useAuftragAnlegen({
    client: p.client,
    kundeId: ziel.kundeId,
    companies: ziel.companies,
    vorbelegung: ziel.vorbelegung,
    feldPraefix: "bauf-neu",
    onSchliessen: p.onAngelegt,
    onNeuLaden: p.onNeuLaden,
    onBearbeiten: p.onBearbeiten,
  });
  const { entwurf, setEntwurf, vorbelegtOfferId } = anlage;

  const umfeld = useAuftraegeUmfeld(p.client, ziel.kundeId);

  /**
   * Der EINE Ausgang neben dem Anlegen (Hausregel „striktes Modalpanel", 06.09.2026): kein
   * Ausgang unter laufendem Schreiben, Rückfrage, sobald etwas im Formular steht, das der
   * Ausgangsstand nicht hatte (bei einer Anlage aus einem Angebot ist das der VORBELEGTE
   * Stand — siehe `ausgang` in useAuftragAnlegen). „Anderen Kunden wählen" geht denselben
   * Weg: Es verlässt die Maske genauso, nur zurück auf den ersten Schritt.
   */
  function abbrechen(weiter: () => void): void {
    if (anlage.speichert) return;
    if (!darfAbbrechen(!entwuerfeGleich(entwurf, anlage.ausgang))) return;
    weiter();
  }

  return (
    <Modal breite="weit" abbrechenGesperrt={anlage.speichert}
      titel={`Neuer Auftrag für ${ziel.kundeName}`}
      onAbbrechen={() => abbrechen(p.onAbbrechen)}>
      {/* Der Abschnitt behält seinen Namen: Er ist die Maske IM Panel. Den Rahmen bringt das
          Panel mit — die Zusatzklasse nimmt ihn hier weg, während der ERSTE Schritt (Kunde
          wählen) ihn im Inhalt weiter trägt. */}
      <section className="bauf__anlage bauf__anlage--im-panel"
        aria-label={vorbelegtOfferId !== null ? "Auftrag aus Angebot" : "Neuer Auftrag"}>
        {/* Aus einem Angebot abgeleitet steht der Kunde FEST — ihn zu wechseln hiesse, die
            Herkunft zu verlieren. Dann führt nur „Abbrechen" heraus, und die Kopfzeile
            entfällt ganz: Seit der Titel im Panel steht, hielte sie nichts mehr. */}
        {ziel.vorbelegung === null && (
          <div className="bauf__anlage-kopf">
            <button type="button" className="kauf__sekundaer" disabled={anlage.speichert}
              onClick={() => abbrechen(p.onAnderenKunden)}>
              Anderen Kunden wählen
            </button>
          </div>
        )}

        {vorbelegtOfferId !== null && (
          <p className="bauf__hinweis" role="status">
            Vorbelegt aus einem angenommenen Angebot — die Laufzeit noch ergänzen.
          </p>
        )}

        {ziel.companies.length === 0 && (
          <p className="bauf__hinweis" role="status">
            Für diesen Kunden besteht noch keine Unternehmensbeziehung — ohne sie lässt sich
            kein Auftrag anlegen.
          </p>
        )}

        {/* Ohne Positionsblock — wie in der Akte: Am noch nicht angelegten Auftrag gibt es
            keinen Vorgang, an dem Positionen hängen könnten. */}
        {auftragFormularFelder({
          praefix: "bauf-neu", mitUnternehmen: true, companies: ziel.companies, umfeld,
          entwurf, setEntwurf, hatPositionen: false, feldFehler: anlage.feldFehler,
          kopf: anlage.kopf,
          // Festlegung Z10: aus einem Angebot abgeleitet, ist die Periodenregel gesperrt —
          // sie kommt von dort, nicht aus einer zweiten Wahl im Anlegeformular.
          periodBasisGesperrt: vorbelegtOfferId !== null,
          vorschauWerte: auftragVorschauWerte({
            entwurf, companies: ziel.companies, kontakte: umfeld.kontakte,
            kundeName: ziel.kundeName,
          }),
        })}

      </section>

      <ModalFuss speichernText="Anlegen" speichernGesperrt={anlage.speichert}
        abbrechenGesperrt={anlage.speichert}
        onSpeichern={() => { void anlage.anlegen(); }}
        onAbbrechen={() => abbrechen(p.onAbbrechen)} />
    </Modal>
  );
}
